"use client";

import { Layers, RotateCw } from "lucide-react";
import { useRef, useState } from "react";
import ActivityFrame from "./ActivityFrame";

export interface Flashcard {
  front: string;
  back: string;
}

export default function FlashcardDeck({
  title,
  cards,
  onComplete,
}: {
  title: string;
  cards: Flashcard[];
  // Called once when every card has been flipped (deck "completed")
  onComplete?: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const seen = useRef<Set<number>>(new Set());
  const completed = useRef(false);

  if (cards.length === 0) return null;
  const card = cards[index];

  const flip = () => {
    setFlipped((f) => !f);
    seen.current.add(index);
    if (!completed.current && seen.current.size === cards.length) {
      completed.current = true;
      onComplete?.();
    }
  };

  const advance = (delta: number) => {
    setFlipped(false);
    setIndex((i) => (i + delta + cards.length) % cards.length);
  };

  return (
    <ActivityFrame
      type="flashcards"
      kindLabel="Flashcards"
      icon={Layers}
      title={title}
      meta={`${index + 1} / ${cards.length}`}
    >
      <button
        onClick={flip}
        className="flashcard"
        aria-label={flipped ? "Show front of card" : "Flip card over"}
      >
        <span className={`flashcard__inner ${flipped ? "is-flipped" : ""}`}>
          {/* Hide the non-visible face from assistive tech so the back isn't
              read out while the front is showing (both faces stay in the DOM
              for the CSS flip). */}
          <span className="flashcard__face flashcard__front" aria-hidden={flipped}>
            {/* Neutral Front/Back, not Question/Answer — decks are just as
                often term/definition, and it matches the builder's columns */}
            <span className="flashcard__tag">Front</span>
            <span className="flashcard__text">{card.front}</span>
            <span className="flashcard__hint">
              <RotateCw className="h-3 w-3" /> tap to flip
            </span>
          </span>
          <span className="flashcard__face flashcard__back" aria-hidden={!flipped}>
            <span className="flashcard__tag">Back</span>
            <span className="flashcard__text">{card.back}</span>
          </span>
        </span>
      </button>

      <div className="mt-3 flex items-center justify-between">
        <button onClick={() => advance(-1)} className="activity__btn">
          ← Previous
        </button>
        <div className="flex gap-1">
          {cards.map((_, i) => (
            <span
              key={i}
              className={`activity__dot ${i === index ? "is-active" : ""}`}
            />
          ))}
        </div>
        <button onClick={() => advance(1)} className="activity__btn">
          Next →
        </button>
      </div>
    </ActivityFrame>
  );
}
